import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { BOTS, computeStats, byYear, MONTH_ORDER } from "@/data/performance";

const fmt = (v, signed = false) => `${signed && v >= 0 ? "+" : ""}${v.toFixed(1)}%`;

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="border border-white/15 bg-[#0A0A0C]/95 px-4 py-3 backdrop-blur-xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            <p className="mt-1 font-mono text-sm text-gold">${payload[0].value.toFixed(2)}k</p>
        </div>
    );
};

const Performance = () => {
    const [botId, setBotId] = useState(BOTS[0].id);
    const bot = BOTS.find((b) => b.id === botId);
    const stats = useMemo(() => computeStats(bot.months), [bot]);
    const years = useMemo(() => byYear(bot.months), [bot]);

    const cards = [
        ["Total return", fmt(stats.totalReturn, true), "text-emerald-400"],
        ["Max drawdown", `−${stats.maxDD.toFixed(1)}%`, "text-red-400"],
        ["Win rate (months)", fmt(stats.winRate), "text-white"],
        ["Profit factor", stats.profitFactor.toFixed(2), "text-white"],
        ["Best month", fmt(stats.bestMonth, true), "text-emerald-400"],
        ["Avg monthly", fmt(stats.avgMonthly, true), "text-gold"],
    ];

    return (
        <section
            id="performance"
            data-testid="performance-section"
            className="scroll-mt-32 border-t border-white/10 bg-coal/30"
        >
            <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
                <Reveal className="flex flex-wrap items-end justify-between gap-8">
                    <div>
                        <Eyebrow>Track record</Eyebrow>
                        <h2 className="mt-6 max-w-2xl font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
                            Numbers first.
                            <span className="font-editorial italic font-light text-gold"> Then the pitch.</span>
                        </h2>
                    </div>
                    <p className="max-w-md text-sm leading-relaxed text-zinc-400">
                        24 months of self-reported performance per bot — including the drawdowns.
                        Simulated on a $100k reference account, compounded monthly.
                    </p>
                </Reveal>

                <Reveal delay={0.1} className="mt-12">
                    <div className="flex flex-wrap gap-2" data-testid="performance-bot-tabs">
                        {BOTS.map((b) => (
                            <button
                                key={b.id}
                                data-testid={`performance-tab-${b.id}`}
                                onClick={() => setBotId(b.id)}
                                className={`border px-6 py-3 text-left transition-colors duration-300 ${
                                    b.id === botId
                                        ? "border-gold/60 bg-gold/[0.06] text-white"
                                        : "border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"
                                }`}
                            >
                                <span className="block font-display text-sm font-extrabold tracking-tight">{b.name}</span>
                                <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                    {b.market}
                                </span>
                            </button>
                        ))}
                    </div>
                    <p className="mt-4 font-editorial italic font-light text-lg text-zinc-400" data-testid="performance-strategy">
                        {bot.strategy}
                    </p>
                </Reveal>

                <Reveal delay={0.15} className="mt-10">
                    <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3 lg:grid-cols-6">
                        {cards.map(([label, value, color]) => (
                            <div key={label} data-testid={`performance-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-obsidian p-6">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
                                <p className={`mt-3 font-mono text-2xl font-medium tracking-tight ${color}`}>{value}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.2} className="mt-6">
                    <div className="border border-white/10 bg-black/40 p-6 md:p-8" data-testid="performance-equity-chart">
                        <div className="flex items-center justify-between">
                            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                Equity curve — $100k reference
                            </p>
                            <p className="font-mono text-[11px] text-gold">
                                ${stats.curve[stats.curve.length - 1].equity.toFixed(1)}k
                            </p>
                        </div>
                        <div className="mt-6 h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={stats.curve} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.28} />
                                            <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                                    <XAxis
                                        dataKey="label"
                                        interval={3}
                                        tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                                        axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        domain={["dataMin - 4", "dataMax + 4"]}
                                        width={44}
                                        tickFormatter={(v) => `$${Math.round(v)}k`}
                                        tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(212,175,55,0.4)" }} />
                                    <Area
                                        type="monotone"
                                        dataKey="equity"
                                        stroke="#D4AF37"
                                        strokeWidth={2}
                                        fill="url(#goldFill)"
                                        isAnimationActive={true}
                                        animationDuration={900}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.25} className="mt-6">
                    <div className="overflow-x-auto border border-white/10 bg-black/40" data-lenis-prevent>
                        <table className="w-full min-w-[860px] text-right" data-testid="performance-monthly-table">
                            <thead>
                                <tr className="border-b border-white/10">
                                    <th className="px-5 py-4 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Year</th>
                                    {MONTH_ORDER.map((m) => (
                                        <th key={m} className="px-2 py-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{m}</th>
                                    ))}
                                    <th className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">YTD</th>
                                </tr>
                            </thead>
                            <tbody>
                                {years.map(({ year, rows, ytd }) => (
                                    <tr key={year} className="border-b border-white/5 last:border-0">
                                        <td className="px-5 py-4 text-left font-mono text-xs text-white">{year}</td>
                                        {MONTH_ORDER.map((m) => {
                                            const cell = rows.find((r) => r.month === m);
                                            return (
                                                <td
                                                    key={m}
                                                    className={`px-2 py-4 font-mono text-xs ${
                                                        !cell ? "text-zinc-700" : cell.r >= 0 ? "text-emerald-400" : "text-red-400"
                                                    }`}
                                                >
                                                    {cell ? fmt(cell.r, true) : "—"}
                                                </td>
                                            );
                                        })}
                                        <td className={`px-5 py-4 font-mono text-xs font-medium ${ytd >= 0 ? "text-gold" : "text-red-400"}`}>
                                            {fmt(ytd, true)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Reveal>

                <Reveal delay={0.3} className="mt-10 flex flex-wrap items-center justify-between gap-8">
                    <p className="max-w-2xl font-mono text-[11px] leading-relaxed text-zinc-600" data-testid="performance-methodology">
                        Methodology: figures are derived from backtested and forward-tested records on a
                        simulated $100k account, net of typical spread and commission assumptions, compounded
                        monthly. Results are self-reported and unaudited. Past performance is not indicative
                        of future results.
                    </p>
                    <Link
                        to="/contact?interest=Forex%20Trading%20Bots"
                        data-testid="performance-enquire"
                        className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-8 py-3.5 text-sm text-gold transition-colors duration-300 hover:bg-gold hover:text-black"
                    >
                        Request the full record <ArrowUpRight size={15} />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
};

export default Performance;
